/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import { Grid, Box } from '@mui/material';
import { useEffect, useState } from 'react';
import './style.css';
import { ENVIRONMENT } from 'config';
import { useSelector } from 'react-redux';
import { fetchExternalText } from 'modules/common/utils/external-content';
import { isRequestCanceled } from 'modules/common/utils/request';
import { selectAppTheme } from '../../dashboard/selectors';
import variants from '../../common/theme/variants';
/**
 * User manual page define here
 * @returns
 */
const UserManual = () => {
  const [content, setContent] = useState(null);
  const [headings, setHeadings] = useState([]);
  //
  const themeName = useSelector(selectAppTheme);
  const selectedVariant = variants.find((variant) => variant.name === themeName);
  //
  const getHtmlContent = async (blobUrl, signal) => {
    try {
      let htmlContent = await fetchExternalText(blobUrl, { signal });
      htmlContent = htmlContent.replace(/images/g, `${ENVIRONMENT.USER_MANUAL_URL}/images`);

      // Add font family styles globally
      const styleTag = `
          <style>
            body, p, div, h1, h2, h3, h4, h5, h6, span {
              font-family: 'Lexend' !important; 
            }
          </style>
          `;
      // Inject the style tag at the beginning of the HTML content
      htmlContent = styleTag + htmlContent;
      setContent(htmlContent);
    } catch (error) {
      if (isRequestCanceled(error)) return;
      console.error('Error fetching the HTML content:', error);
    }
  };
  useEffect(() => {
    const controller = new AbortController();
    getHtmlContent(`${ENVIRONMENT.USER_MANUAL_URL}/user-manual.html`, controller.signal);
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (content != null) {
      // Grab the div element by ID
      const testDiv = document.getElementById('test');
      if (testDiv) {
        // Grab all h1 to h5 tags inside the div
        const hTags = testDiv.querySelectorAll('h1, h2, h3, h4, h5');
        // Map the tags to an array of objects with id and text content
        const headingsData = Array.from(hTags).map((tag) => {
          // Generate a random ID if the tag doesn't have one
          if (!tag.id) {
            tag.id = new Date().getMilliseconds;
          }
          return {
            id: tag.id,
            text: tag.querySelector('span')?.textContent || tag.textContent,
            tag: tag.tagName.toLowerCase(),
          };
        });
        setHeadings(headingsData);
      }
    }
  }, [content]);

  const scrollToSection = (id) => {
    // Scroll to the section with the given id
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };
  //
  return (
    <Grid container spacing={2} direction="row">
      <Grid
        item
        xs={3}
        sx={{
          position: 'fixed',
          left: 0,
          top: 0,
          height: '100vh',
          backgroundColor: selectedVariant?.header.background,
          paddingTop: 5,
          paddingRight: 5,
        }}
      >
        <div>
          <ul className="user-manual-ul">
            {headings.map((heading) => {
              let headingClass = '';

              if (heading.tag === 'h1') headingClass = 'heading-h1';
              if (heading.tag === 'h2') headingClass = 'heading-h2';
              if (heading.tag === 'h3') headingClass = 'heading-h3';
              if (heading.tag === 'h4') headingClass = 'heading-h4';

              return (
                <li
                  className={`user-manual-li ${headingClass}`}
                  onClick={() => scrollToSection(heading.id)}
                  key={heading.id}
                  style={{
                    cursor: 'pointer',
                    fontSize: heading.tag === 'h1' ? '18px' : '15px',
                  }}
                >
                  <span
                    style={{
                      fontFamily: 'Lexend',
                      color: heading.tag === 'h1' ? 'white' : '#9e9e9e',
                    }}
                  >
                    {heading.text}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      </Grid>
      <Grid
        item
        xs={9}
        justifyContent="space-between"
        sx={{
          marginTop: 5,
          marginLeft: '35%',
          marginRight: '10%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            height: '100vh',
          }}
        >
          {/* eslint-disable-next-line react/no-danger -- user manual HTML is loaded from a trusted internal URL */}
          <div id="test" dangerouslySetInnerHTML={{ __html: content }} />
        </Box>
      </Grid>
    </Grid>
  );
};
//
export default UserManual;
